import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Heart, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SocialConnectionsGuide() {
  const navigate = useNavigate();

  const connectionIdeas = [
    { action: "Schedule a friend date", desc: "Put it in the calendar—coffee, walk, dinner" },
    { action: "Send an unexpected text", desc: "'I was thinking of you' goes a long way" },
    { action: "Call instead of text", desc: "Hearing someone's voice deepens connection" },
    { action: "Join a group activity", desc: "Book club, fitness class, volunteer group" },
    { action: "Host a small gathering", desc: "Doesn't need to be fancy—just intentional" },
    { action: "Reconnect with old friends", desc: "Social media makes this easier than ever" }
  ];

  const qualityMarkers = [
    "You can be yourself around them",
    "They celebrate your wins genuinely",
    "They're there during hard times",
    "You feel energized, not drained, after seeing them",
    "There's mutual give and take",
    "You can have honest conversations"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 10 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Social Connections Audit
          </h1>
          <p className="text-xl text-secondary-600">Nurture the Relationships That Matter</p>
        </div>

        <section className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Users className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Why Connection Matters</h3>
              <p className="text-pink-100 text-lg">
                Strong relationships are the #1 predictor of longevity—more than diet, exercise, or genetics. 
                People with strong social bonds live 50% longer. This week, we invest in connection.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            Signs of Quality Friendships
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {qualityMarkers.map((marker, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-pink-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-pink-500 flex-shrink-0" />
                {marker}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-pink-500" />
            Ways to Connect This Week
          </h2>
          <div className="space-y-3">
            {connectionIdeas.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-pink-700 mb-1">{item.action}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SocialConnectionsGuide;
